import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  FormGroup,
  RadioGroup,
  Radio,
  Alert,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CustomInputTextField from 'components/shared/custom_input_textfield';

export const equipmentOptions = [
  'Balança',
  'Câmaras frias',
  'Banheiros',
  'Pias',
  'Bancas de exposição',
  'Outro',
];

export const frequencyOptions = [
  'Diária',
  'Semanal',
  'Quinzenal',
  'Mensal',
  'Outro',
];

export const publicOptions = [
  'Consumidores locais',
  'Turistas',
];

export interface PermanentMarket {
  name: string;
  address: string;
  neighborhood: string;
  zone: 'Rural' | 'Urbana' | '';
  hasInfrastructure: boolean | null;
  equipments: string[];
  otherEquipment: string;
  frequency: string;
  otherFrequency: string;
  predominantPublic: string;
  numFamilyFarmProducers: number;
  numWomenFamilyFarmProducers: number;
  numFamilyFarmResellers: number;
  numWomenFamilyFarmResellers: number;
  numHandicraftSellers: number;
  numWomenHandicraftSellers: number;
  numExternalIndustrialSellers: number;
  annualMarketValue: number;
  totalFamilyFarmingValue: number;
  totalMarketValue: number;
}

export interface Step3SimData {
  referenceYear: number;
  permanentMarkets: PermanentMarket[];
}

interface Step3Props {
  onNext: () => void;
  onBack: () => void;
  onChange: (data: Step3SimData) => void;
  data: Step3SimData;
  disabled?: boolean;
  referenceYear: number;
  /** Quantidade de feiras permanentes declarada na Etapa 2: 0 = não exige cadastros; >0 = exige exatamente essa quantidade */
  requiredCount: number;
}

export const emptyMarket: PermanentMarket = {
  name: '',
  address: '',
  neighborhood: '',
  zone: '',
  hasInfrastructure: null,
  equipments: [],
  otherEquipment: '',
  frequency: '',
  otherFrequency: '',
  predominantPublic: '',
  numFamilyFarmProducers: 0,
  numWomenFamilyFarmProducers: 0,
  numFamilyFarmResellers: 0,
  numWomenFamilyFarmResellers: 0,
  numHandicraftSellers: 0,
  numWomenHandicraftSellers: 0,
  numExternalIndustrialSellers: 0,
  annualMarketValue: 0,
  totalFamilyFarmingValue: 0,
  totalMarketValue: 0,
};

export const formatMonetaryInput = (value: string): string => {
  const cleaned = value.replace(/\D/g, '');
  if (!cleaned) return '';
  const num = parseFloat(cleaned) / 100;
  return num.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const parseMonetaryValue = (formattedValue: string): number => {
  const parsable = formattedValue.replace(/\./g, '').replace(',', '.');
  const value = parseFloat(parsable);
  return isNaN(value) ? 0 : Math.round(value * 100);
};

export const formatCurrency = (value: number | undefined) =>
  typeof value === 'number' && !isNaN(value)
    ? (value / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : 'Não informado';

export const validateRequiredField = (field: any, message: string) => field ? '' : message;

export const validateNonNegativeField = (field: number, message: string) => field < 0 ? message : '';

export const validateMarket = (mkt: PermanentMarket) => {
  const newErrors: { [key: string]: string } = {};

  // Step 1: Validate required fields
  newErrors.name = validateRequiredField(mkt.name, 'Campo obrigatório');
  newErrors.address = validateRequiredField(mkt.address, 'Campo obrigatório');
  newErrors.neighborhood = validateRequiredField(mkt.neighborhood, 'Campo obrigatório');
  newErrors.zone = validateRequiredField(mkt.zone, 'Campo obrigatório');
  newErrors.frequency = validateRequiredField(mkt.frequency, 'Campo obrigatório');
  newErrors.predominantPublic = validateRequiredField(mkt.predominantPublic, 'Campo obrigatório');

  // Step 2: Validate specific fields based on conditions
  if (!mkt.equipments || mkt.equipments.length === 0) newErrors.equipments = 'Selecione pelo menos um equipamento';
  if (mkt.equipments?.includes('Outro') && !mkt.otherEquipment) newErrors.otherEquipment = 'Informe o outro equipamento';
  if (mkt.frequency === 'Outro' && !mkt.otherFrequency) newErrors.otherFrequency = 'Informe a frequência';

  // Step 3: Validate non-negative numeric fields
  newErrors.numFamilyFarmProducers = validateNonNegativeField(mkt.numFamilyFarmProducers, 'Obrigatório');
  newErrors.numWomenFamilyFarmProducers = validateNonNegativeField(mkt.numWomenFamilyFarmProducers, 'Obrigatório');
  newErrors.numFamilyFarmResellers = validateNonNegativeField(mkt.numFamilyFarmResellers, 'Obrigatório');
  newErrors.numWomenFamilyFarmResellers = validateNonNegativeField(mkt.numWomenFamilyFarmResellers, 'Obrigatório');
  newErrors.numHandicraftSellers = validateNonNegativeField(mkt.numHandicraftSellers, 'Obrigatório');
  newErrors.numWomenHandicraftSellers = validateNonNegativeField(mkt.numWomenHandicraftSellers, 'Obrigatório');
  newErrors.numExternalIndustrialSellers = validateNonNegativeField(mkt.numExternalIndustrialSellers, 'Obrigatório');
  newErrors.totalFamilyFarmingValue = validateNonNegativeField(mkt.totalFamilyFarmingValue, 'Obrigatório');
  newErrors.totalMarketValue = validateNonNegativeField(mkt.totalMarketValue, 'Obrigatório');

  return newErrors;
};

const Step3: React.FC<Step3Props> = ({
  onNext,
  onBack,
  onChange,
  data,
  disabled = false,
  referenceYear,
  requiredCount = 0,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const count = data.permanentMarkets?.length ?? 0;
  const addButtonDisabled =
    disabled || requiredCount === 0 || count >= requiredCount;
  const canContinue =
    requiredCount === 0 ? true : count === requiredCount;
  const [openDialog, setOpenDialog] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [market, setMarket] = useState<PermanentMarket>(emptyMarket);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Estados locais para preservar a digitação dos valores monetários
  const [totalFamilyFarmingValueInput, setTotalFamilyFarmingValueInput] = useState('0.00');
  const [totalMarketValueInput, setTotalMarketValueInput] = useState('0.00');

  const handleMarketsChange = (markets: PermanentMarket[]) => {
    onChange({
      referenceYear,
      permanentMarkets: markets,
    });
  };

  const handleOpenDialog = (editMarket?: PermanentMarket, index?: number) => {
    setMarket(editMarket ? { ...editMarket } : emptyMarket);

    // Formata os valores monetários para exibição
    if (editMarket) {
      setTotalFamilyFarmingValueInput(
        (editMarket.totalFamilyFarmingValue / 100).toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
        })
      );
      setTotalMarketValueInput(
        (editMarket.totalMarketValue / 100).toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
        })
      );
    } else {
      setTotalFamilyFarmingValueInput('0.00');
      setTotalMarketValueInput('0.00');
    }

    setErrors({});
    setEditIndex(typeof index === 'number' ? index : null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setErrors({});
    setEditIndex(null);
  };

  const handleMarketChange = (field: keyof PermanentMarket, value: any) => {
    setMarket((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleEquipmentChange = (equipment: string) => {
    setMarket((prev) => {
      const alreadySelected = prev.equipments.includes(equipment);
      return {
        ...prev,
        equipments: alreadySelected
          ? prev.equipments.filter((e) => e !== equipment)
          : [...prev.equipments, equipment],
      };
    });
  };

  const handleTotalFamilyFarmingValueChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const formatted = formatMonetaryInput(e.target.value);
    setTotalFamilyFarmingValueInput(formatted);
    const centavos = parseMonetaryValue(formatted);
    handleMarketChange('totalFamilyFarmingValue', centavos);
  };

  const handleTotalMarketValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatMonetaryInput(e.target.value);
    setTotalMarketValueInput(formatted);
    const centavos = parseMonetaryValue(formatted);
    handleMarketChange('totalMarketValue', centavos);
  };



  const handleSaveMarket = () => {
    const validation = validateMarket(market);
    setErrors(validation);

    if (Object.values(validation).some(Boolean)) return;

    const updatedMarkets = [...(data.permanentMarkets || [])];
    if (editIndex !== null) {
      updatedMarkets[editIndex] = market;
    } else {
      updatedMarkets.push(market);
    }
    handleMarketsChange(updatedMarkets);
    setOpenDialog(false);
    setEditIndex(null);
  };


  const handleRemoveMarket = (index: number) => {
    const updated = [...(data.permanentMarkets || [])];
    updated.splice(index, 1);
    handleMarketsChange(updated);
  };

  return (
    <Box>
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Feiras Permanentes cadastradas</Typography>
          <Button variant="contained" onClick={() => handleOpenDialog()} disabled={addButtonDisabled}>
            Adicionar Feira Permanente
          </Button>
        </Box>
        {(!data.permanentMarkets || data.permanentMarkets.length === 0) && (
          <Typography color="text.secondary">Nenhuma feira cadastrada.</Typography>
        )}
        <Box>
          {data?.permanentMarkets?.map((mkt, idx) => (
            <Paper key={idx} sx={{ p: 2, mb: 2, position: 'relative' }}>
              <IconButton
                aria-label="Editar"
                onClick={() => handleOpenDialog(mkt, idx)}
                disabled={disabled}
                sx={{ position: 'absolute', top: 8, right: 48 }}
              >
                <EditIcon />
              </IconButton>
              <IconButton
                onClick={() => handleRemoveMarket(idx)}
                sx={{ position: 'absolute', top: 8, right: 8 }}
                aria-label="Remover"
                disabled={disabled}
              >
                <DeleteIcon />
              </IconButton>
              <Typography variant="subtitle2"><b>{mkt.name}</b> - {mkt.neighborhood} ({mkt.zone})</Typography>
              <Typography variant="body2">Endereço: {mkt.address}</Typography>
              <Typography variant="body2">Infraestrutura física: {mkt.hasInfrastructure ? 'Sim' : 'Não'}</Typography>
              <Typography variant="body2">
                Equipamentos: {mkt.equipments?.join(', ')} {mkt.equipments?.includes('Outro') && mkt.otherEquipment ? `(${mkt.otherEquipment})` : ''}
              </Typography>
              <Typography variant="body2">
                Frequência: {mkt.frequency} {mkt.frequency === 'Outro' && mkt.otherFrequency ? `(${mkt.otherFrequency})` : ''}
              </Typography>
              <Typography variant="body2">Público predominante: {mkt.predominantPublic}</Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="body2">Comerciantes/produtores da Agricultura Familiar: {mkt.numFamilyFarmProducers}</Typography>
              <Typography variant="body2">Mulheres comerciantes/produtoras da Agricultura Familiar: {mkt.numWomenFamilyFarmProducers}</Typography>
              <Typography variant="body2">Comerciantes da Agricultura Familiar (Revendedores): {mkt.numFamilyFarmResellers}</Typography>
              <Typography variant="body2">Mulheres comerciantes (revendedoras) da Agricultura Familiar: {mkt.numWomenFamilyFarmResellers}</Typography>
              <Typography variant="body2">Comerciantes de artesanato: {mkt.numHandicraftSellers}</Typography>
              <Typography variant="body2">Mulheres comerciantes de artesanato: {mkt.numWomenHandicraftSellers}</Typography>
              <Typography variant="body2">Comerciantes de produtos industrializados de origem externa: {mkt.numExternalIndustrialSellers}</Typography>
              {/*<Typography variant="body2">Valor comercializado nas feiras do município anualmente: {formatCurrency(mkt.annualMarketValue)}</Typography>*/}
              <Typography variant="body2">Valor total comercializado pela Agricultura Familiar: {formatCurrency(mkt.totalFamilyFarmingValue)}</Typography>
              <Typography variant="body2">Valor total comercializado na feira: {formatCurrency(mkt.totalMarketValue)}</Typography>
            </Paper>
          ))}
        </Box>

      </Paper>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editIndex !== null ? 'Editar Feira Permanente' : 'Adicionar Feira Permanente'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Nome da feira"
                value={market.name}
                error={!!errors.name}
                onChange={e => handleMarketChange('name', e.target.value.toUpperCase())}
                fullWidth
                helperText={errors.name}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Endereço da feira"
                value={market.address}
                onChange={e => handleMarketChange('address', e.target.value.toUpperCase())}
                fullWidth
                error={!!errors.address}
                helperText={errors.address}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Bairro/localidade"
                value={market.neighborhood}
                onChange={e => handleMarketChange('neighborhood', e.target.value.toUpperCase())}
                error={!!errors.neighborhood}
                helperText={errors.neighborhood}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                label="Zona"
                required
                value={market.zone}
                onChange={e => handleMarketChange('zone', e.target.value)}
                error={!!errors.zone}
                helperText={errors.zone}
                fullWidth
              >
                <MenuItem value="Rural">Rural</MenuItem>
                <MenuItem value="Urbana">Urbana</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography sx={{ mb: 1 }}>Quais equipamentos estão disponíveis?</Typography>
              <FormGroup row>
                {equipmentOptions.map(option => (
                  <FormControlLabel
                    key={option}
                    control={
                      <Checkbox
                        checked={market.equipments.includes(option)}
                        onChange={() => handleEquipmentChange(option)}
                        name={option}
                      />
                    }
                    label={option}
                  />
                ))}
              </FormGroup>
              {errors.equipments && <Typography color="error">{errors.equipments}</Typography>}
              {market.equipments.includes('Outro') && (
                <TextField
                  label="Outro (qual?)"
                  value={market.otherEquipment}
                  onChange={e => handleMarketChange('otherEquipment', e.target.value)}
                  error={!!errors.otherEquipment}
                  helperText={errors.otherEquipment}
                  fullWidth
                  margin="dense"
                  required
                  sx={{ mt: 2 }}
                />
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography sx={{ mb: 1 }}>A feira ocorre com frequência:</Typography>
              <RadioGroup

                value={market.frequency}
                row
                onChange={e => handleMarketChange('frequency', e.target.value)}
              >
                {frequencyOptions.map(option => (
                  <FormControlLabel
                    key={option}
                    control={<Radio />}
                    value={option}
                    label={option}
                  />
                ))}
              </RadioGroup>
              {errors.frequency && <Typography color="error">{errors.frequency}</Typography>}
              {market.frequency === 'Outro' && (
                <TextField
                  label="Especifique a frequência"
                  value={market.otherFrequency}
                  error={!!errors.otherFrequency}
                  onChange={e => handleMarketChange('otherFrequency', e.target.value)}
                  margin="dense"
                  helperText={errors.otherFrequency}
                  fullWidth
                  required
                  sx={{ mt: 2 }}
                />
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                label="Qual o público predominante?"
                value={market.predominantPublic}
                onChange={e => handleMarketChange('predominantPublic', e.target.value)}
                fullWidth
                error={!!errors.predominantPublic}
                helperText={errors.predominantPublic}
                required
              >
                {publicOptions.map(option => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <CustomInputTextField
                label="Comerciantes/produtores da Agricultura Familiar"
                type="number"
                inputProps={{ min: 0 }}
                value={market.numFamilyFarmProducers}
                onChange={e => handleMarketChange('numFamilyFarmProducers', Number(e.target.value))}
                error={!!errors.numFamilyFarmProducers}
                margin="dense"
                helperText={errors}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CustomInputTextField
                label="Mulheres comerciantes/produtoras da Agricultura Familiar"
                inputProps={{ min: 0 }}
                type="number"
                value={market.numWomenFamilyFarmProducers}
                onChange={e => handleMarketChange('numWomenFamilyFarmProducers', Number(e.target.value))}
                error={errors}
                fullWidth
                helperText={errors}
                margin="dense"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CustomInputTextField
                label="Comerciantes da Agricultura Familiar (Revendedores)"
                type="number"
                inputProps={{ min: 0 }}
                value={market.numFamilyFarmResellers}
                onChange={e => handleMarketChange('numFamilyFarmResellers', Number(e.target.value))}
                error={errors}
                helperText={errors}
                fullWidth
                margin="dense"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CustomInputTextField
                label="Mulheres comerciantes (revendedoras) da Agricultura Familiar"
                type="number"
                inputProps={{ min: 0 }}
                value={market.numWomenFamilyFarmResellers}
                onChange={e => handleMarketChange('numWomenFamilyFarmResellers', Number(e.target.value))}
                error={errors}
                helperText={errors}
                fullWidth
                margin="dense"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CustomInputTextField
                label="Comerciantes de artesanato"
                type="number"
                inputProps={{ min: 0 }}
                value={market.numHandicraftSellers}
                onChange={e => handleMarketChange('numHandicraftSellers', Number(e.target.value))}
                error={errors}
                helperText={errors}
                fullWidth
                margin="dense"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CustomInputTextField
                label="Mulheres comerciantes de artesanato"
                type="number"
                inputProps={{ min: 0 }}
                value={market.numWomenHandicraftSellers}
                onChange={e => handleMarketChange('numWomenHandicraftSellers', Number(e.target.value))}
                error={errors}
                helperText={errors}
                fullWidth
                margin="dense"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CustomInputTextField
                label="Quantos são comerciantes de produtos beneficiados/industrializados? "
                type="number"
                inputProps={{ min: 0 }}
                value={market.numExternalIndustrialSellers}
                onChange={e => handleMarketChange('numExternalIndustrialSellers', Number(e.target.value))}
                error={errors}
                helperText={errors}
                fullWidth
                margin="dense"
                required
              />
            </Grid>
            {/*<Grid item xs={12} md={6}>
                            <TextField
                                label="Valor comercializado nas feiras do município anualmente (R$)"
                                value={formatCurrency(market.annualMarketValue)}
                                onChange={handleCurrencyChange('annualMarketValue')}
                                error={!!errors.annualMarketValue}
                                helperText={errors.annualMarketValue}
                                fullWidth
                                required
                                inputProps={{ inputMode: 'numeric' }}
                            />
                        </Grid>*/}
            <Grid item xs={12} md={6}>
              <TextField
                label="Valor total comercializado pela Agricultura Familiar (R$)"
                value={totalFamilyFarmingValueInput}
                onChange={handleTotalFamilyFarmingValueChange}
                error={!!errors.totalFamilyFarmingValue}
                helperText={errors.totalFamilyFarmingValue}
                fullWidth
                required
                inputProps={{ inputMode: 'numeric' }}
                InputProps={{
                  startAdornment: <span style={{ marginRight: 4 }}>R$</span>,
                }}
                placeholder="0,00"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Valor total comercializado na feira anualmente (R$)"
                value={totalMarketValueInput}
                onChange={handleTotalMarketValueChange}
                error={!!errors.totalMarketValue}
                helperText={errors.totalMarketValue}
                fullWidth
                required
                inputProps={{ inputMode: 'numeric' }}
                InputProps={{
                  startAdornment: <span style={{ marginRight: 4 }}>R$</span>,
                }}
                placeholder="0,00"
              />
            </Grid>
          </Grid>
          <Alert severity="info" sx={{ mt: 3 }}>
            Todos os campos são obrigatórios. O Ano de referência do Questionário será associado automaticamente.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSaveMarket} variant="contained">Salvar</Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ justifyContent: 'space-between', display: 'flex', mt: 3 }}>
        <Button variant="outlined" onClick={onBack} disabled={disabled}>
          Voltar
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            if (disabled) return;
            if (!canContinue) {
              enqueueSnackbar(
                'Não é possível continuar. Adicione as feiras permanentes declaradas na Etapa 2.',
                { variant: 'warning' },
              );
              return;
            }
            onNext();
          }}
          size="large"
          disabled={disabled}
          sx={
            !canContinue && !disabled
              ? { opacity: 0.7, cursor: 'not-allowed' }
              : undefined
          }
        >
          Salvar e continuar
        </Button>
      </Box>
    </Box>
  );
};

export default Step3;
